/* eslint-disable react/display-name */
import React, { Component } from 'react'

import { ConnectedRouter } from 'connected-react-router'
import Cookies from 'js-cookie'
import { parse } from 'query-string'
import { Route, Switch } from 'react-router-dom'

import { ThemeProvider } from '@driftt/dds'
import { Icon } from '@driftt/tide-icons'
import {
  deleteAccessToken,
  getAccessToken,
  setAccessToken,
} from '@driftt/web-auth'

import config from 'config'
import { INVITE_CODE_COOKIE } from 'constants/Cookies'
import { DealCodeCookie } from 'constants/dealConstants'
import { Flow, RBAC_FLOWS } from 'constants/flowConstants'
import Billing from 'layouts/Billing'
import Deal from 'layouts/Deal'
import DealError from 'layouts/DealError'
import DealExpired from 'layouts/DealExpired'
import DealPending from 'layouts/DealPending'
import DealUpgradeSuccessStep from 'layouts/DealUpgradeSuccessStep'
import InviteFlowController from 'layouts/InviteFlowController'
import LoadingPage from 'layouts/LoadingPage'
import Setup from 'layouts/Setup'
import SetupAwaitVerification from 'layouts/SetupAwaitVerification'
import TouchlessStart from 'layouts/TouchlessStart'
import VideoController from 'layouts/VideoController'
import {
  hasSegmentSnippet,
  installSegment,
  installWidgetStub,
} from 'utils/snippets'
import { setVideoSignupReferrer } from 'utils/trackingUtils'

import './style.css'

const connectWithRouter = (WrappedComponent) => (props) => {
  const { history, ...rest } = props
  return (
    <ConnectedRouter history={history} {...rest}>
      <WrappedComponent {...props} />
    </ConnectedRouter>
  )
}

const InviteError = () => (
  <div className="app-loading-placeholder">
    <Icon
      name="drift-logo-round"
      width={100}
      height={100}
      className="start-drift-logo"
    />
    <h2>Something went wrong</h2>
    <h4>Please have your administrator generate a new invitation</h4>
  </div>
)
class App extends Component {
  async UNSAFE_componentWillMount() {
    const { history, actions, isGdprConsentRequired } = this.props

    if (!hasSegmentSnippet()) {
      installWidgetStub()
      installSegment()
      window.analytics && window.analytics.load(config.SEGMENT_ID)
    }

    const {
      email,
      resetToken,
      token,
      sso,
      dealCode,
      flow,
      isCalendaringSeat,
      thirdPartySignup,
      plan,
      coupon,
      access_type,
      login_hint,
      relay,
    } = parse(history.location.search)

    if (access_type) {
      setVideoSignupReferrer({ source: access_type, location: 'root' })
    }
    if (login_hint) {
      actions.setEmail(login_hint)
    }

    Cookies.remove('control')

    // allow rbac flows to use social sign on
    actions.setHasSocialInvite(!!flow)

    if (flow) {
      actions.setFlow(flow)
    } else if (history.location.pathname.includes('video')) {
      actions.setFlow(Flow.VIDEO)
    } else if (history.location.pathname.includes('playbooks')) {
      actions.setFlow(Flow.PLAYBOOKS)
    } else if (history.location.pathname.includes('reporting')) {
      actions.setFlow(Flow.REPORTING)
    } else if (history.location.pathname.includes('deal') || dealCode) {
      // deal and invoice
      actions.setFlow(Flow.DEAL)
    } else {
      actions.setFlow(Flow.CHAT)
    }

    if (plan) {
      this.props.actions.setPlan(plan)
    }

    if (coupon) {
      this.props.actions.setCoupon(coupon)
    }

    if (resetToken) {
      Cookies.set(INVITE_CODE_COOKIE, resetToken)
      this.redirectActivatedUserToSetup({
        flow,
        token: resetToken,
        isCalendaringSeat,
      })
    } else if (token && thirdPartySignup) {
      setAccessToken({ token: token })
      this.redirectThirdPartyUserToSetup()
    } else if (token) {
      setAccessToken({ token: token })
    } else if (sso) {
      this.redirectSsoUserToSetup({ email, isCalendaringSeat })
    } else if (dealCode) {
      this.redirectDealCode(dealCode)
    } else if (isGdprConsentRequired) {
      if (email) {
        deleteAccessToken()
        actions.setEmail(email)
      }
      return this.redirectSignup()
    } else if (email) {
      deleteAccessToken()
      actions.setEmail(email)
      actions.setDisabledEmail(true)
      actions.signup({ email })
      return this.redirectSignup()
    } else if (flow === Flow.HYFY_MIGRATION) {
      actions.fetchEmailFromRelay(relay)
      actions.setDisabledEmail(true)
      return this.redirectVideoSignup()
    } else if (history.location.pathname === '/') {
      this.redirectRoot()
    }
  }

  redirectActivatedUserToSetup = ({ flow, token, isCalendaringSeat }) => {
    const { history, actions } = this.props

    deleteAccessToken()
    actions.setResetToken(token)

    if (flow === Flow.VIDEO) {
      history.push('/setup/password')
    } else if (isCalendaringSeat) {
      history.push('/setup/calendaring/activate')
    } else if (flow === Flow.PLAYBOOKS) {
      history.push('/setup/enterprise/activate/playbooks')
    } else if (flow === Flow.REPORTING) {
      history.push('/setup/enterprise/activate/reporting')
    } else if (RBAC_FLOWS.indexOf(flow) > -1) {
      history.push('/invite')
    } else {
      history.push('/setup')
    }
  }

  redirectSsoUserToSetup = ({ email, isCalendaringSeat }) => {
    const { history, actions } = this.props

    if (getAccessToken()) {
      actions.fetchUser()
      actions.setSSOProvisionedFlow(true)
      if (isCalendaringSeat) {
        history.push('/setup/calendaring/activate')
      } else {
        history.push('/setup')
      }
    } else {
      actions.setEmail(email)
      actions.getLoginUrl()
    }
  }

  redirectThirdPartyUserToSetup = () => {
    const { history } = this.props
    history.push('/setup/touchless/custom')
  }

  redirectDealCode = (dealCode) => {
    const { history } = this.props

    if (dealCode) {
      Cookies.set(DealCodeCookie, dealCode)
      history.push('/deal')
    }
  }

  redirectRoot = () => {
    const { history } = this.props
    history.push({ pathname: '/start', search: history.location.search })
  }

  redirectSignup = () => {
    const { history } = this.props
    history.push({ pathname: '/start/signup', search: history.location.search })
  }

  redirectVideoSignup = () => {
    const { history } = this.props
    history.push({
      pathname: '/start/signup/video',
      search: history.location.search,
    })
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.ssoRedirectError && this.props.ssoRedirectError) {
      this.props.history.push('/invite-error')
    }
  }

  render() {
    return (
      <ThemeProvider>
        <Switch>
          <Route path="/verification" component={SetupAwaitVerification} />
          <Route path="/setup" component={Setup} />
          <Route path="/video" component={VideoController} />
          <Route path="/start" component={TouchlessStart} />
          <Route path="/invite" component={InviteFlowController} />
          <Route path="/deal" component={Deal} />
          <Route path="/deal-expired" component={DealExpired} />
          <Route path="/deal-error" component={DealError} />
          <Route path="/deal-pending" component={DealPending} />
          <Route path="/deal-upgraded" component={DealUpgradeSuccessStep} />
          <Route path="/invite-error" component={InviteError} />
          <Route path="/billing" component={Billing} />
          <Route path="/" component={LoadingPage} />
        </Switch>
      </ThemeProvider>
    )
  }
}

export default connectWithRouter(App)
